<template>
  <transition name="fade">
    <div class="modal-mask">
      <div class="modal-wrapper">
        <focus-trap v-model="uploadOptions.active">
          <div
            class="modal-container"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-label"
          >
            <div id="modal-label" class="sr-only">
              {{ $t("modal_label_WCAG") }}
            </div>
            <div class="modal-header">
              <slot name="header">
                <div class="default-header f-clear">
                  <div class="modal-dropdown-container f-left font-bold">
                    <button
                      v-if="state !== 'upload'"
                      @click="backHandler"
                      class="
                        back-button
                        flex
                        items-center
                        justify-center
                        w-8
                        h-8
                        text-secondary-700
                        bg-secondary-200
                        focus-visible:ring
                        hover-bg-primary-200
                        rounded-full
                      "
                    >
                      <arrow-left class="h-2.5"></arrow-left>
                    </button>
                    <div v-if="state === 'upload'">
                      {{ $t("modal_uploader_header", [titleText]) }}
                    </div>
                    <div v-else-if="state === 'library'">
                      {{ $t("image_library") }}
                    </div>
                    <div v-else-if="state === 'rename'">
                      {{ $t("editing") }}
                    </div>
                  </div>
                  <a href="#" class="f-right close" @click.prevent="closeModal">
                    <div>
                      <close-icon></close-icon>
                    </div>
                  </a>
                </div>
              </slot>
            </div>

            <div class="modal-body">
              <slot name="body">
                <div
                  class="default-body"
                  v-bind:class="{
                    'p-6': state != 'library',
                  }"
                >
                  <uploader
                    @files="logoImg"
                    @edit="editName"
                    @delete="deleteFile"
                    :translations="uploaderText"
                    v-if="state === 'upload'"
                    :showSuccess="false"
                    ><cert-library-button
                      v-if="editorData.product === 'mtm'"
                      :button-text="$t('image_library')"
                      @click="openImageLibModal"
                      class="mr-4"
                    ></cert-library-button
                  ></uploader>
                  <cert-file-name-section
                    v-else-if="state === 'rename'"
                    :value="modalData.certificate_template[fileNameProp]"
                    @input="setNewFileName"
                  ></cert-file-name-section>
                  <uploader-success
                    :name="modalData.certificate_template[fileNameProp]"
                    :preview-image="base64"
                    :showButtons="showEditButtons"
                    @edit="editName"
                    @delete="deleteLibFile"
                    v-if="showUploaderSuccess && state === 'upload'"
                  ></uploader-success>
                  <vue-custom-scrollbar
                    class="scroll-area"
                    :settings="settings"
                    @ps-scroll-y="handleScroll"
                    v-else-if="state === 'library'"
                  >
                    <div
                      v-for="(item, i) in imageList"
                      :key="i"
                      @click="selectImage(item)"
                      v-bind:class="{
                        'border-selected': selectedImageID == item.ID,
                        'mt-4': i > 0,
                      }"
                      class="
                        flex
                        p-4
                        border-2
                        rounded-xl
                        border-solid
                        text-link
                        border-grey
                        cursor-pointer
                        h-4p5
                      "
                    >
                      <div class="flex space-x-4">
                        <div class="relative">
                          <img
                            class="
                              w-10
                              h-10
                              mr-4
                              border border-solid border-grey
                              rounded-lg
                            "
                            :src="item.URL"
                          />
                          <div
                            class="
                              absolute
                              bottom-check
                              right-check
                              rounded-full
                              bg-neutral-white
                              p-1p5
                            "
                          ></div>
                        </div>
                        <div class="flex items-center text-14">
                          <div>{{ item.FileName }}{{ item.FileType }}</div>
                          <div class="text-12"></div>
                        </div>
                      </div>
                    </div>
                  </vue-custom-scrollbar>
                </div>
              </slot>
            </div>

            <div class="modal-footer">
              <slot name="footer">
                <div
                  v-if="state == 'upload'"
                  class="default-footer flex w-full justify-end"
                >
                  <cert-cancel-button
                    :button-text="$t('cancel')"
                    @click="closeModal"
                    class="mr-4"
                  ></cert-cancel-button>
                  <cert-confirm-button
                    :button-text="$t('save_modal')"
                    @click="saveAndCloseModal"
                    class="footer-button"
                    :button-disabled="!isFileUploaded"
                  ></cert-confirm-button>
                </div>
                <div
                  v-else-if="state == 'library'"
                  class="default-footer flex w-full justify-end"
                >
                  <cert-cancel-button
                    :button-text="$t('cancel')"
                    @click="closeLibraryModal"
                    class="mr-4"
                  ></cert-cancel-button>
                  <cert-confirm-button
                    :button-text="$t('choose')"
                    @click="chooseImage"
                    class="footer-button"
                    :button-disabled="selectedImageID == null"
                  ></cert-confirm-button>
                </div>
                <div
                  v-else-if="state == 'rename'"
                  class="default-footer flex w-full justify-end"
                >
                  <cert-cancel-button
                    :button-text="$t('cancel')"
                    @click="closeRenameModal"
                    class="mr-4"
                  ></cert-cancel-button>
                  <cert-confirm-button
                    :button-text="$t('save_modal')"
                    @click="saveRenameImage"
                    class="footer-button"
                    :button-disabled="!isFileUploaded"
                  ></cert-confirm-button>
                </div>
              </slot>
            </div>
          </div>
        </focus-trap>
      </div>
    </div>
  </transition>
</template>

<script>
import { FocusTrap } from "focus-trap-vue";
import CertConfirmButton from "../form/CertConfirmButton.vue";
import CertCancelButton from "../form/CertCancelButton.vue";
import CertFileNameSection from "../form/CertFileNameSection.vue";
import Uploader from "@/components/General/Uploader.vue";
import UploaderSuccess from "@/components/General/UploaderSuccess.vue";
import CertLibraryButton from "../form/CertLibraryButton.vue";
import CloseIcon from "@/assets/icon-close.svg";
import ArrowLeft from "@/assets/arrow-left.svg";
import { getMtmImageLibrary } from "@/utilities/api";
import vueCustomScrollbar from "vue-custom-scrollbar";
import "vue-custom-scrollbar/dist/vueScrollbar.css";

export default {
  name: "CertUploader",
  props: {
    modalData: Object,
    editorData: Object,
    uploadOptions: Object,
  },
  data() {
    return {
      state: "upload",
      base64: null,
      isFileUploaded: false,
      pageNumber: 1,
      selectedImageID: null,
      selectedImage: Object,
      imageList: [],
      mtmData: Object,
      isDataReady: true,
      showUploaderSuccess: false,
      previewLibImage: null,
      showEditButtons: true,
      imageType: "logo",
      settings: {
        suppressScrollY: false,
        suppressScrollX: false,
        wheelPropagation: false,
      },
    };
  },
  components: {
    FocusTrap,
    CertConfirmButton,
    CertCancelButton,
    CertFileNameSection,
    CloseIcon,
    Uploader,
    UploaderSuccess,
    CertLibraryButton,
    ArrowLeft,
    vueCustomScrollbar,
  },
  computed: {
    fileNameProp() {
      return this.uploadOptions.editedValue + "_fname";
    },
    fileIdProp() {
      return this.uploadOptions.editedValue + "_id";
    },
    titleText() {
      switch (this.uploadOptions.editedValue) {
        case "logo":
          return this.$t("logo");
        case "logo_opt":
          return this.$t("seal");
        case "signature_logo":
          return this.$t("signature");
        case "signature_logo_opt":
          return this.$t("signature2");
      }

      return "";
    },
    uploaderText() {
      return {
        title: this.$t("uploader_title"),
        file_description: this.$t("uploader_file_description"),
        uploader_description: this.$t("uploader_uploader_description"),
        upload: this.$t("uploader_upload"),
        upload_link: this.$t("uploader_upload_link"),
        error_title: this.$t("uploader_error_title"),
        error_max: this.$t("uploader_error_max"),
        error_size: this.$t("uploader_error_size"),
        error_format: this.$t("uploader_error_format"),
      };
    },
  },

  methods: {
    backHandler() {
      this.state = "upload";
    },
    editName() {
      this.state = "rename";
    },

    setNewFileName(text) {
      this.modalData.certificate_template[this.fileNameProp] = text;
    },
    saveRenameImage() {
      this.state = "upload";
      //this.showUploaderSuccess = true;
    },
    openImageLibModal() {
      this.state = "library";
      if (
        this.uploadOptions.editedValue == "logo" ||
        this.uploadOptions.editedValue == "logo_opt"
      ) {
        this.imageType = "logo";
      } else if (
        this.uploadOptions.editedValue == "signature_logo" ||
        this.uploadOptions.editedValue == "signature_logo_opt"
      ) {
        this.imageType = "Signature";
      }
      this.getImageList();
    },

    handleScroll: function (e) {
      if (
        e.target.clientHeight +
          e.target.scrollTop +
          e.target.clientHeight * 0.3 >=
        e.target.scrollHeight
      ) {
        if (this.isDataReady) {
          this.isDataReady = false;
          this.pageNumber = this.pageNumber + 1;
          this.getImageList();
        }
      }
    },
    getImageList() {
      getMtmImageLibrary(
        this.editorData.token,
        this.editorData.libraryId,
        this.pageNumber,
        this.imageType
      ).then((data) => {
        if (!data.onError) {
          this.mtmData = data;
          this.imageList.push(...data.response.data.Data);
          this.isDataReady = true;
        } else {
          console.log("error");
        }
      });
    },
    saveAndCloseModal() {
      this.modalData.certificate_template[this.uploadOptions.editedValue] =
        this.base64;

      this.closeModal();
    },
    selectImage(image) {
      this.selectedImage = image;
      this.selectedImageID = image.ID;
    },
    chooseImage() {
      this.getBase64Image(this.selectedImage.URL, this.setBase64);
      this.state = "upload";
      this.modalData.certificate_template[this.fileNameProp] =
        this.selectedImage.FileName;
      this.modalData.certificate_template[this.fileIdProp] =
        this.selectedImage.ID;
      this.showUploaderSuccess = true;
      this.isFileUploaded = true;
      this.showEditButtons = false;
    },
    getBase64Image(url, callback) {
      var xhr = new XMLHttpRequest();
      xhr.onload = function () {
        var reader = new FileReader();
        reader.onloadend = function () {
          callback(reader.result);
        };
        reader.readAsDataURL(xhr.response);
      };
      xhr.open("GET", url);
      xhr.responseType = "blob";
      xhr.send();
    },
    setBase64(data) {
      this.base64 = data;
    },
    logoImg(files) {
      if (!files) return;
      const file = files[0];
      this.modalData.certificate_template[this.fileNameProp] =
        file.name.replace(/\.[^/.]+$/, "");
      this.modalData.certificate_template[this.fileIdProp] =
        this.createNewGUID();

      this.toBase64(file).then((base64) => {
        this.base64 = base64;
        this.isFileUploaded = true;
      });
      this.showUploaderSuccess = true;
      this.showEditButtons = true;
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    createNewGUID() {
      let a, b;
      for (
        b = a = "";
        a++ < 36;
        b +=
          (a * 51) & 52
            ? (a ^ 15 ? 8 ^ (Math.random() * (a ^ 20 ? 16 : 4)) : 4).toString(
                16
              )
            : "-"
      );
      return b;
    },
    deleteFile() {
      this.base64 = null;
      this.isFileUploaded = false;
      //debug it
    },
    deleteLibFile() {
      this.selectedImage = null;
      this.selectedImageID = null;
      this.previewLibImage = null;
      this.base64 = null;
      this.showUploaderSuccess = false;
      this.isFileUploaded = false;
    },
    closeModal() {
      this.uploadOptions.active = false;
      this.cleared = false;
      if (this.uploadOptions.focusBackElem instanceof HTMLElement) {
        this.uploadOptions.focusBackElem.focus();
      }
      this.isFileUploaded = false;
      this.showUploaderSuccess = false;
    },
    closeLibraryModal() {
      this.selectedImageID = null;
      this.selectedImage = null;
      this.state = "upload";
      this.showUploaderSuccess = false;
    },
    closeRenameModal() {
      this.state = "upload";
    },
    getWidth() {
      const documentWidth = document.documentElement.clientWidth;
      return Math.abs(window.innerWidth - documentWidth);
    },
  },
  created() {
    document.body.style.paddingRight = `${this.getWidth()}px`;
    document.body.style.overflowY = "hidden";
  },
  destroyed() {
    document.body.style.overflowY = "";
    document.body.style.paddingRight = "";
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/tailwind.scss";
.mtm {
  .modal-container .default-header .close {
    &:hover,
    &:focus {
      color: $exMtm;

      > div {
        border-color: $exMtm;
      }
    }
  }
}
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: block;
  transition: opacity 0.3s ease;
  overflow-y: auto;
}

.modal-wrapper {
  //   display: table-cell;
  //   vertical-align: middle;
  margin-top: 45px;
}

.back-button {
  border: 0;
  margin-right: 16px;
  cursor: pointer;
}

.modal-container {
  width: 844px;
  margin: 0px auto;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: $dropShadow;
  transition: all 0.3s ease;

  .default-header {
    padding: 24px 32px 0 0;

    .close {
      color: $exBlack;
      font-weight: bold;
      @include fontSize(11px);
      position: relative;
      padding-right: 45px;
      > div {
        height: 31px;
        width: 31px;
        border: 1px solid $borderLightGray;
        border-radius: 50%;
        display: inline-block;
        position: absolute;
        top: -4px;
        right: 0;
        padding: 9px;
      }

      &:hover,
      &:focus {
        color: $exBlue;

        > div {
          border-color: $exBlue;

          > img {
            filter: invert(19%) sepia(93%) saturate(2569%) hue-rotate(208deg)
              brightness(92%) contrast(102%);
          }
        }
      }
    }

    .modal-dropdown-container {
      margin-left: 30px;
      width: 270px;
      padding-bottom: 24px;
      display: flex;
      align-items: center;
    }

    ul {
      > li {
        width: 110px;
        height: 50px;
        vertical-align: bottom;
        a {
          display: block;
          height: 100%;
          width: 100%;
          text-decoration: none;
          color: $exBlack;
          font-weight: bold;
          text-align: center;
          @include fontSize(14px);
          background-color: #fff;
          border: 0;
          border-bottom: 3px solid transparent;
          cursor: pointer;

          .default {
            font-weight: normal;
            @include fontSize(12px);
          }
        }

        a.active {
          border-bottom-color: $exBlue;
          color: $exBlue;
        }

        a:focus,
        a:active {
          color: $exBlueActive;
        }
      }
    }
  }
  .scroll-area {
    position: relative;
    padding: 24px;
    height: 50vh;
  }
  .default-body {
    border-top: 1px solid $borderLightGray;
    border-bottom: 1px solid $borderLightGray;

    .input-title {
      color: $exBlack;
      @include fontSize(14px);
      font-weight: bold;
      line-height: 17px;
      margin-bottom: 8px;
    }

    input[type="text"] {
      width: 100%;
      height: 50px;
      border: 1px solid $borderLightGray;
      color: $exBlack;
      border-radius: $borderRadius;
      color: $exBlack;
      padding: 16px 48px 16px 16px;
    }

    .input-container {
      position: relative;

      > a {
        position: absolute;
        top: 14px;
        right: 16px;
        text-decoration: none;
        color: $exBlack;

        > img {
          vertical-align: middle;
        }

        > img:hover,
        img:focus {
          filter: invert(19%) sepia(93%) saturate(2569%) hue-rotate(208deg)
            brightness(92%) contrast(102%);
        }
      }
    }
  }

  .default-footer {
    padding: 16px 32px;
    .footer-button {
    }
  }
}
</style>
